import React from 'react';
import { ITheme, Icon, Stack, Text } from '@fluentui/react';
import {
    SreHealthMetric,
    SreInflightFacets,
} from '../../../../../AGSS/models/SreHealth';

interface ISreStageBreakdownProps {
    facets: SreInflightFacets;
    metrics?: SreHealthMetric[];
    metricsScope?: 'filtered' | 'fleet';
    theme: ITheme;
    isOpen: boolean;
    activeStages?: number[];
    onToggleOpen: () => void;
    onToggleStage: (stageNumber: number) => void;
}

/**
 * Stage and state facet counts, collapsed by default.
 *
 * Rows are clickable filters. On the previous screen the equivalent table was read-only and had no
 * connection to the list below it, so spotting a stage with 40 stuck items gave you no way to act
 * on that.
 */
export const SreStageBreakdown: React.FC<ISreStageBreakdownProps> = ({
    facets,
    metrics,
    metricsScope = 'filtered',
    theme,
    isOpen,
    activeStages,
    onToggleOpen,
    onToggleStage,
}) => {
    const totalStages = facets.byStage.length;

    return (
        <Stack
            styles={{
                root: {
                    border: `1px solid ${theme.palette.neutralLight}`,
                    borderRadius: '4px',
                    backgroundColor: theme.palette.white,
                },
            }}
        >
            <Stack
                as="button"
                aria-expanded={isOpen}
                aria-label="Stage breakdown"
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
                styles={{
                    root: {
                        padding: '8px 12px',
                        border: 0,
                        background: 'transparent',
                        color: 'inherit',
                        font: 'inherit',
                        textAlign: 'left',
                        width: '100%',
                        cursor: 'pointer',
                        userSelect: 'none',
                    },
                }}
                onClick={onToggleOpen}
            >
                <Icon iconName={isOpen ? 'ChevronDown' : 'ChevronRight'} />
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    Stage breakdown
                </Text>
                <Text
                    variant="small"
                    styles={{ root: { color: theme.palette.neutralSecondary } }}
                >
                    {totalStages} {totalStages === 1 ? 'stage' : 'stages'}
                </Text>
            </Stack>

            {isOpen && metrics && (
                <div style={{ overflowX: 'auto', padding: 12 }}>
                    <Text variant="small">
                        {metricsScope === 'fleet'
                            ? 'Fleet metrics (legacy snapshot)'
                            : 'Metrics for the current filter'}
                    </Text>
                    <table
                        aria-label="Health metrics by orchestration type, stage and state"
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            minWidth: 600,
                        }}
                    >
                        <thead>
                            <tr>
                                {[
                                    'Orchestration type',
                                    'Stage',
                                    'State',
                                    'Count',
                                    'Average retries',
                                    'Maximum retries',
                                ].map((heading) => (
                                    <th
                                        key={heading}
                                        scope="col"
                                        style={{
                                            padding: 8,
                                            textAlign: 'left',
                                            borderBottom: `1px solid ${theme.palette.neutralLight}`,
                                        }}
                                    >
                                        {heading}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.map((metric) => (
                                <tr
                                    key={`${metric.orchestrationType}:${metric.stage}:${metric.stageState}`}
                                >
                                    <td style={{ padding: 8 }}>
                                        {metric.orchestrationType}
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        {metric.stageNumber !== undefined
                                            ? `${metric.stageNumber} · `
                                            : ''}
                                        {metric.stage}
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        {metric.stageState}
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        {metric.count}
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        {metric.avgRetryCount.toFixed(2)}
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        {metric.maxRetryCount}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isOpen && (
                <Stack
                    horizontal
                    wrap
                    tokens={{ childrenGap: 24 }}
                    styles={{ root: { padding: '0 12px 12px 12px' } }}
                >
                    <Stack
                        styles={{
                            root: {
                                minWidth: 0,
                                flex: '1 1 280px',
                                maxWidth: '100%',
                            },
                        }}
                    >
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    color: theme.palette.neutralSecondary,
                                    marginBottom: '4px',
                                },
                            }}
                        >
                            By stage
                        </Text>
                        {facets.byStage.map((bucket) => {
                            const isActive = activeStages?.includes(
                                bucket.number ?? -1
                            );
                            return (
                                <Stack
                                    as={
                                        bucket.number != null &&
                                        bucket.number >= 0
                                            ? 'button'
                                            : 'div'
                                    }
                                    aria-pressed={Boolean(isActive)}
                                    key={`${bucket.number}-${bucket.name}`}
                                    horizontal
                                    verticalAlign="center"
                                    tokens={{ childrenGap: 8 }}
                                    title={
                                        bucket.order
                                            ? `${bucket.name} — stage ${bucket.number}, step ${bucket.order}`
                                            : bucket.name
                                    }
                                    styles={{
                                        root: {
                                            padding: '4px 6px',
                                            border: 0,
                                            width: '100%',
                                            font: 'inherit',
                                            color: 'inherit',
                                            textAlign: 'left',
                                            borderRadius: '2px',
                                            cursor: 'pointer',
                                            backgroundColor: isActive
                                                ? theme.palette.themeLighter
                                                : 'transparent',
                                            selectors: {
                                                ':hover': {
                                                    backgroundColor:
                                                        theme.palette
                                                            .neutralLighter,
                                                },
                                            },
                                        },
                                    }}
                                    onClick={() =>
                                        bucket.number !== null &&
                                        bucket.number !== undefined &&
                                        bucket.number >= 0 &&
                                        onToggleStage(bucket.number)
                                    }
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                width: '28px',
                                                color: theme.palette
                                                    .neutralSecondary,
                                            },
                                        }}
                                    >
                                        {bucket.number ?? ''}
                                    </Text>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                flex: 1,
                                                minWidth: 0,
                                                overflowWrap: 'anywhere',
                                            },
                                        }}
                                    >
                                        {bucket.name}
                                    </Text>
                                    <Text
                                        variant="small"
                                        styles={{ root: { fontWeight: 600 } }}
                                    >
                                        {bucket.count}
                                    </Text>
                                </Stack>
                            );
                        })}
                    </Stack>

                    <Stack
                        styles={{
                            root: {
                                minWidth: 0,
                                flex: '0 1 220px',
                                maxWidth: '100%',
                            },
                        }}
                    >
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    color: theme.palette.neutralSecondary,
                                    marginBottom: '4px',
                                },
                            }}
                        >
                            By state
                        </Text>
                        {facets.byStageState.map((bucket) => (
                            <Stack
                                key={bucket.name}
                                horizontal
                                tokens={{ childrenGap: 8 }}
                                styles={{ root: { padding: '4px 6px' } }}
                            >
                                <Text
                                    variant="small"
                                    styles={{ root: { flex: 1 } }}
                                >
                                    {bucket.name}
                                </Text>
                                <Text
                                    variant="small"
                                    styles={{ root: { fontWeight: 600 } }}
                                >
                                    {bucket.count}
                                </Text>
                            </Stack>
                        ))}

                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    color: theme.palette.neutralSecondary,
                                    margin: '12px 0 4px 0',
                                },
                            }}
                        >
                            By orchestration type
                        </Text>
                        {facets.byOrchestrationType.map((bucket) => (
                            <Stack
                                key={bucket.name}
                                horizontal
                                tokens={{ childrenGap: 8 }}
                                styles={{ root: { padding: '4px 6px' } }}
                            >
                                <Text
                                    variant="small"
                                    styles={{ root: { flex: 1 } }}
                                >
                                    {bucket.name || 'Unknown'}
                                </Text>
                                <Text
                                    variant="small"
                                    styles={{ root: { fontWeight: 600 } }}
                                >
                                    {bucket.count}
                                </Text>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};
